import { Box, ColorSwatch, Group, SimpleGrid, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import ConfidenceBadge from './ConfidenceBadge';
import LogoOnBackground from './LogoOnBackground';
import { COLOR_ROLES, DARK_ROLES } from './brandIdentity';
import classes from './BrandIdentity.module.css';

// The brand kit at a glance: most of it was read from the site and is right, so it is shown
// read-only and only what the reader was unsure about is flagged. A swatch opens the editor on it.

const groupLabel = (text, extra) => (
  <Group gap={6} mb={6}>
    <Text size="xs" tt="uppercase" fw={600} c="dimmed" style={{ letterSpacing: '0.04em' }}>
      {text}
    </Text>
    {extra}
  </Group>
);

const CheckDot = () => (
  <Tooltip label="Please check: we were not sure about this one." withArrow>
    <Box
      role="img"
      aria-label="Please check"
      w={8}
      h={8}
      flex="none"
      style={{ borderRadius: '50%', background: 'var(--mantine-color-yellow-6)' }}
    />
  </Tooltip>
);

const Chip = ({ token, label, ariaLabel = label, hex, swatch, low, small, active, onEdit, roleHandlers }) => (
  <UnstyledButton
    className={`${classes.chip} ${small ? classes.chipSmall : ''}`}
    data-active={active ? 'true' : undefined}
    // The name carries what the chip shows, since the label overrides its text for screen readers.
    aria-label={`Edit ${ariaLabel}, ${hex ? hex.toUpperCase() : 'not set'}${low ? ', please check' : ''}`}
    onClick={() => onEdit(token)}
    {...roleHandlers(token)}
  >
    <ColorSwatch color={swatch} size={small ? 18 : 24} flex="none" withShadow />
    <Box miw={0} style={{ flex: 1 }}>
      <Text size={small ? 'xs' : 'sm'} fw={500} truncate>
        {label}
      </Text>
      <Text size="xs" c="dimmed" className={classes.hex} truncate>
        {hex ? hex.toUpperCase() : 'Not set'}
      </Text>
    </Box>
    {low && <CheckDot />}
  </UnstyledButton>
);

const LogoThumb = ({ src, background, label }) => (
  <LogoOnBackground
    w={140}
    title={label}
    src={src}
    label={label}
    background={background}
    height={56}
    padding={8}
    imageHeight={40}
    failedText="Couldn't load"
  />
);

const BrandKitSummary = ({
  visual,
  logoUrl,
  sources,
  previewColors,
  darkShown,
  darkPalette,
  activeRole,
  onEdit,
  roleHandlers,
}) => {
  const colors = visual?.colors || {};
  const darkColors = visual?.darkColors || {};
  const darkIsDerived = !Object.values(darkColors).some(Boolean);
  const low = (key) => sources[key]?.confidence === 'low';
  const toCheck = Object.values(sources).filter((source) => source?.confidence === 'low').length;

  return (
    <Stack gap="md">
      {toCheck > 0 && (
        <Group gap={6}>
          <CheckDot />
          <Text size="sm" c="yellow.8">
            {toCheck === 1
              ? '1 value to check: we were not sure about this one.'
              : `${toCheck} values to check: we were not sure about these.`}
          </Text>
        </Group>
      )}
      <div>
        {groupLabel('Logos')}
        <Group gap="sm" align="flex-start">
          <Stack gap={4}>
            <LogoThumb src={logoUrl} background="#FFFFFF" label="Logo for light backgrounds" />
            <ConfidenceBadge level={sources.logoUrl?.confidence} />
          </Stack>
          <Stack gap={4}>
            <LogoThumb src={visual?.logoDarkUrl} background="#1E1E1E" label="Logo for dark backgrounds" />
            <ConfidenceBadge level={sources.logoDarkUrl?.confidence} />
          </Stack>
        </Group>
      </div>

      <div>
        {groupLabel('Colors')}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={8} verticalSpacing={8}>
          {COLOR_ROLES.map(({ key, label, hint }) => (
            <Tooltip key={key} label={hint} withArrow openDelay={400}>
              <div>
                <Chip
                  token={`colors.${key}`}
                  label={label}
                  hex={colors[key]}
                  swatch={previewColors[key]}
                  low={low(`colors.${key}`)}
                  active={activeRole === `colors.${key}`}
                  onEdit={onEdit}
                  roleHandlers={roleHandlers}
                />
              </div>
            </Tooltip>
          ))}
        </SimpleGrid>
      </div>

      <div>
        {groupLabel(
          'Dark mode',
          darkIsDerived && (
            <Text size="xs" c="dimmed">
              worked out for you
            </Text>
          )
        )}
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing={6} verticalSpacing={6}>
          {DARK_ROLES.map(({ key, label, hint }) => (
            <Tooltip key={key} label={hint} withArrow openDelay={400}>
              <div>
                <Chip
                  small
                  token={`darkColors.${key}`}
                  label={label}
                  ariaLabel={`dark mode ${label}`}
                  hex={darkShown[key]}
                  swatch={darkPalette[key]}
                  low={low('darkColors')}
                  active={activeRole === `darkColors.${key}`}
                  onEdit={onEdit}
                  roleHandlers={roleHandlers}
                />
              </div>
            </Tooltip>
          ))}
        </SimpleGrid>
      </div>

      <Group gap={6} wrap="wrap">
        <Text size="sm" style={{ overflowWrap: 'anywhere' }}>
          <Text span c="dimmed">
            Font{' '}
          </Text>
          {visual?.fontFamily || 'Not set'}
        </Text>
        <ConfidenceBadge level={sources.fontFamily?.confidence} />
        <Text size="sm" c="dimmed">
          ·
        </Text>
        <Text size="sm">
          <Text span c="dimmed">
            Corners{' '}
          </Text>
          {Number.isFinite(visual?.radius) ? `${visual.radius} px` : 'Not set'}
        </Text>
        <ConfidenceBadge level={sources.radius?.confidence} />
      </Group>
    </Stack>
  );
};

export default BrandKitSummary;
